import { useTranslator } from '@bluebeam/react-i18n';
import React from 'react';

type Props = {
  type: 'Sync' | 'Upload';
  source?: string;
  label: string;
  percentComplete: number;
  id: string;
  error: boolean;
  onCancel?: (id: string) => void;
  onRetry?: (id: string) => void;
};

const FileManagerRow: React.FC<Props> = ({
  type,
  source,
  label,
  percentComplete,
  id,
  error,
  onCancel,
  onRetry,
}) => {
  const { translate } = useTranslator(['buttons', 'fileManagerRow']);

  const errorMessage = () => {
    if (type === 'Sync') return translate('fileManagerRow:syncErroMessage');
    if (type === 'Upload')
      return translate('fileManagerRow:uploadErrorMessage');
    return '';
  };

  const processingWord = () => {
    if (type === 'Sync') {
      return translate('fileManagerRow:syncing');
    }
    if (type === 'Upload') {
      return translate('fileManagerRow:uploading');
    }
    if (type === 'Export') {
      return translate('fileManagerRow:exporting');
    }
  };

  const completeType = () => {
    if (type === 'Sync') {
      return translate('fileManagerRow:syncComplete');
    }
    if (type === 'Upload') {
      return translate('fileManagerRow:uploadComplete');
    }
    if (type === 'Export') {
      return translate('fileManagerRow:exportComplete');
    }
  };

  return (
    <div
      style={{
        margin: '0 0',
        height: '64px',
        position: 'relative',
        borderBottom: 'solid 0.5px #dcdfe3',
        background: error
          ? '#fbefef'
          : `linear-gradient(to right, #ffffff ${percentComplete}%, #f4f7fc ${percentComplete}% ${
              100 - percentComplete
            }%)`,
        width: '100%',
      }}
      className="row"
    >
      <div
        className="column"
        style={{
          float: 'left',
          height: '64px',
          flex: '10',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            paddingLeft: '16px',
            marginBottom: '8px',
            marginTop: '12px',
            width: 'auto',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
          className="font-size-4 light black-1 row"
        >
          {source && <div className="medium mr1">{source}:</div>}
          <div>{label}</div>
        </div>

        {(() => {
          if (error)
            return (
              <div className="font-size-2 error-red pl3">{errorMessage()}</div>
            );
          if (percentComplete === 100)
            return (
              <div className="font-size-2 black-2 normal pl3">
                {completeType()}
              </div>
            );
          if (percentComplete === 0)
            return (
              <div
                style={{ paddingLeft: '16px' }}
                className="font-size-2 black-2"
              >
                {translate('fileManagerRow:waiting')}
              </div>
            );
          return (
            <div
              style={{ paddingLeft: '16px' }}
              className="font-size-2 secondary-blue"
            >
              {type === 'Upload' ? `${percentComplete.toFixed()}% -` : ''}
              {processingWord()}
            </div>
          );
        })()}
      </div>
      {(() => {
        if (percentComplete < 100 && onCancel)
          return (
            <div
              style={{ alignSelf: 'center' }}
              className="normal mr3 cursor-pointer"
              onClick={() => onCancel(id)}
            >
              {translate('buttons:cancel')}
            </div>
          );
        if (error && onRetry)
          return (
            <div
              style={{ alignSelf: 'center' }}
              className="normal mr3 cursor-pointer"
              onClick={() => onRetry(id)}
            >
              {translate('buttons:retry')}
            </div>
          );
        return null;
      })()}
    </div>
  );
};

export default FileManagerRow;
